import AUDIO_ASSETS from "./StaticAudioAssets.json";
import CreativeEditorSDK, { UserInterfaceElements } from "@cesdk/cesdk-js";
import PAGE_FORMAT_ASSETS from "./PageFormatAssets.json";
import VIDEO_SCENES_ASSETS from "./StaticVideoScenesAssets.json";
import axios from "axios";
import loadAssetSourceFromContentJSON from "./loadAssetSourceFromContentJSON";
import { useEffect, useRef } from "react";
import { AddAnimationComponent } from "./Components/AddAnimationComponent";
import { createApplyFormatAsset } from "./createApplyFormatAsset";
import { createDefaultApplyAssetScene } from "./defaultApplyAssetScene";
import { caseAssetPath } from "./util";

import {
  formatAssetsToPresets,
  PAGE_FORMATS_INSERT_ENTRY,
  pageFormatI18n,
} from "./PageFormatAssetLibrary";

const CaseComponent = () => {
  const cesdk_container = useRef(null);
  const cesdkRef = useRef(null);

  const fetchAssetsImageUrlsFromAPI = async () => {
    try {
      const response = await axios.get(
        "https://dev-api.wipovtool.com/api/v1/admin/file-upload/list"
      );
      const { data } = response.data;

      const imageUrls = data
        .filter((item) => /\.(png|jpg|jpeg|gif|webp)$/i.test(item.url))
        .map((item) => item.url);
      const videoUrls = data
        .filter((item) => item.url.endsWith(".mp4"))
        .map((item) => item.url);

      return { imageUrls, videoUrls };
    } catch (error) {
      console.error("Error fetching asset URLs:", error);
      return { imageUrls: [], videoUrls: [] };
    }
  };

  useEffect(() => {
    const config = {
      locale: "en", //it
      theme: "light",
      license:
        "sJl2Ov9xqXeJp8WdmVGajxDjfG0DOcAZAmpLwslArnc23NyBPTzjoKsF4ajNP08e",
      i18n: {
        en: {
          "libraries.ly.img.audio.ly.img.audio.label": "Soundstripe",
          ...pageFormatI18n(PAGE_FORMAT_ASSETS.assets),
          "libraries.ly.img.video.templates.label": "Example Templates",
        },
        it: {
          ...pageFormatI18n(PAGE_FORMAT_ASSETS.assets),
          "libraries.ly.img.audio.ly.img.audio.label": "Tracce audio",
          "libraries.ly.img.video.templates.label": "Modello",
          "action.block.delete": "$t(common.delete)",
          "action.block.delete_plural": "$t(common.delete) ({{count}})",
          "action.block.duplicate": "$t(common.duplicate)",
          "action.block.lock": "$t(common.lock)",
          "action.block.unlock": "$t(common.unlock)",
          "action.editText": "$t(common.edit)",
          "action.placeholder.change": "Cambia vincoli $t(common.placeholder)",
          "action.placeholder.create": "Crea $t(common.placeholder)",
          "action.placeholder.remove": "Rimuov $t(common.placeholder)",
          "action.position": "$t(common.position)",
          "common.cmyk": "common.cmyk",
          "component.assetSettings.opacity": "$t(common.opacity)",
          "component.audio.trim.play": "$t(common.play)",
          "component.audio.trim.play.description":
            "$t(common.play) trimmed audio",
          "component.colorPicker.hsl.description":
            "{{componentName}}, $t(common.nearestColorName): {{nearestColorName}}; $t(common.colorValue): hue: {{hue}}°, saturation: {{saturation}}%, lightness: {{lightness}}% ",
          "component.contentFill": "$t(common.fill)",

          "component.contentFill.color": "$t(common.color)",
          "component.contentFill.image": "$t(common.image)",
          "component.contentFill.video": "$t(common.video)",
          "component.fileOperation.save": "$t(common.save)",
          "component.inspectorPositionSelect": "$t(common.position)",
          "component.placeholder.create": "$t(common.placeholder)",
          "property.position": "{{axis}} $t(common.position)",
          "property.strokePosition": "$t(common.position)",
          "component.placeholder.settings":
            "Cambia impostazioni $t(common.placeholder)",
          "component.placeholder.settingsMenu":
            "$t(common.placeholder) settings menu",
          "component.roleSelect": "$t(common.role)",
          "component.roleSelect.description": "Seleziona $t(common.role)",
          "component.timeline.pause.description":
            "$t(common.pause) complete video",
          "component.timeline.play.description":
            "$t(common.play) complete video",
          "component.topbar.back": "$t(common.back)",
          "component.topbar.close": "$t(common.close)",
          "component.undo.redo": "$t(common.redo)",
          "component.undo.undo": "$t(common.undo)",
          "component.video.trim.play": "$t(common.play)",
          "component.video.trim.play.description":
            "$t(common.play) trimmed video",
          "input.gradient.colorStop.description":
            "{{stopType}} {{index}} of {{total}} at {{position}}%. $t(common.nearestColorName): {{colorName}}, $t(common.colorValue): {{colorValue}}.{{action}}",
          "input.page.titleTemplate": "$t(common.page) {{number}}",
          "input.text.advanced": "$t(common.advanced)",
          "input.transform": "$t(common.position) & $t(common.size)",
          "input.unit.tooltip": "Seleziona $t(common.unit)",
          "libraries.ly.img.template.confirmation.abort": "$t(common.cancel)",
          "notification.redo": "$t(common.redo)",
          "notification.undo": "$t(common.undo)",
          "action.cutoutOffset.change": "Cambia $t(input.cutoutOffset)",
          "action.cutoutSmoothing.change": "Cambia $t(input.cutoutSmoothing)",
          "action.cutoutType.change": "Cambia $t(input.cutoutType)",
          "action.strokeStyle.change": "$t(property.strokeStyle.description)",
          "common.rotateAndFlip": "$t(common.rotation) & $t(property.flip)",
          "common.rotation.inUnit": "$t(common.rotation) in gradi",
          "component.librarySettings.templateLibrary":
            "$t(component.templateLibrary)",
          "component.themeSelect.dialog.description":
            "$t(component.themeSelect.dialog)",
          "element.transform.resize": "$t(action.block.resize)",
          "element.transform.rotate": "$t(action.block.rotate)",
          "input.preset.tooltip": "Select $t(input.preset)",
          "property.crop.transform": "$t(block.image)",
          "property.strokePositionAndCornerGeometry":
            "$t(property.strokePosition) & $t(property.strokeCornerGeometry)",
          "action.block.delete_plural": "$t(common.delete) ({{count}})",
          "action.gradient.addStop": "Aggiungi un nuovo stop al {{position}}%",
          "common.height.inUnit": "Altezza in {{unit}}",
          "common.width.inUnit": "Larghezza in {{unit}}",
          "component.colorPicker.colorItem": "Colore item {{index}}",
          "component.colorPicker.colorItem.description":
            "Cliccare per usare questo colore ({{color}})",
          "component.colorPicker.colorItem.transparent":
            "Colore item {{index}}, Nome Colore: transparent",
          "component.colorPicker.hsl.description":
            "{{componentName}}, $t(common.nearestColorName): {{nearestColorName}}; $t(common.colorValue): tinta: {{hue}}°, saturazione: {{saturation}}%, luminosità: {{lightness}}% ",
          "component.zoom.shortcut": "o premi {{shortcut}}",
          "component.zoom.to": "{{percentage}}% Zoom",
          "error.upload.sizeExceeded":
            "Il file supera la dimensione massima di {{limit}}x{{limit}}",
          "input.fontSelect": "Font selezionato: {{font}}",
          "input.fontStyle.toggle": "Attiva/disattiva {{style}} style",
          "input.gradient.colorStop.description":
            "{{stopType}} {{index}} di {{total}} al {{position}}%. $t(common.nearestColorName): {{colorName}}, $t(common.colorValue): {{colorValue}}.{{action}}",
          "input.keyShortcut":
            "Per aprire '{{input}}', premere '{{keyShortcut}}'",
          "input.multiSelection.title": "Elementi multipli ({{count}})",
          "input.page.titleTemplate": "$t(common.page) {{number}}",
          "input.propertyToggle.disable": "Disabilita {{property}}",
          "input.propertyToggle.enable": "Abilita {{property}}",
          "input.rename": "Rinomina {{designElementName}}",
          "input.resolution.select": "Seleziona {{which}}",
          "input.typefaceSelect.description":
            "Carattere tipografico selezionato: {{fontFamily}}",
          "property.blendMode.description":
            "Modalità di fusione selezionata: {{mode}}",
          "property.crop.offset": "{{axis}} Offset",
          "property.crop.offset.description":
            "Offset del ritaglio su {{axis}}-axis in {{unit}}",
          "property.crop.size.description":
            "{{dimension}} of the crop frame in {{unit}}",
          "property.orientation.description":
            "Imposta l'orientamento della pagina su {{orientation}}",
          "property.position": "{{axis}} $t(common.position)",
          "property.position.description":
            "Posizione su {{axis}}-axis in {{unit}}",
          "action.align": "Allinea",
          "action.align.bottom": "Inferiore",
          "action.align.bottom.description": "Allinea in basso",
          "action.align.elements": "Allinea elementi",
          "action.align.horizontalCenter": "Centrato orizzontalmente",
          "action.align.horizontalCenter.description":
            "Allinea al centro (orizzontale)",
          "action.align.left": "Sinistra",
          "action.align.left.description": "Allinea a sinistra",
          "action.align.right": "Destra",
          "action.align.right.description": "Allinea a destra",
          "action.align.top": "Superiore",
          "action.align.top.description": "Allinea in alto",
          "action.align.toPage": "Allinea al blocco",
          "action.align.verticalCenter": "Centro verticale",
          "action.align.verticalCenter.description":
            "Allinea al centro (verticale)",
          "action.arrange": "Sposta",
          "action.arrange.bringForward": "Porta avanti",
          "action.arrange.sendBackward": "Manda indietro",
          "action.arrange.toBack": "Manda in fondo",
          "action.arrange.toFront": "Porta in primo piano",
          "action.audio.delete": "Elimina audio",
          "action.audio.duplicate": "action.audio.duplicate",
          "action.audio.replace": "Sostituisci audio",
          "action.block.add": "Aggiungi elemento",
          "action.block.copy": "Copia elemento",
          "action.block.flipX": "Capovolgi elemento (orizzontale)",
          "action.block.flipY": "Capovolgi elemento (verticale)",
          "action.block.lock.description": "Blocca elemento",
          "action.block.move": "Sposta elemento",
          "action.block.paste": "Incolla elemento",
          "action.block.rename": "Rinomina elemento",
          "action.block.resize": "Ridimensiona elemento",
          "action.block.rotate": "Ruota elemento",
          "action.block.toggleVisibility": "(S)Nascondi elemento",
          "action.block.unlock.description": "Sblocca elemento",
          "action.crop.exit": "Esci dalla modalità ritaglio",
          "action.crop.mirrorX": "Specchia immagine orizzontalmente",
          "action.crop.mirrorY": "Specchia immagine verticalmente",
          "action.crop.reset": "Ripristina impostazioni ritaglio",
          "action.crop.turn": "Ruota immagine di 90° in senso antiorario",
          "action.crop.turnAndMirror": "Ruota e specchia",
          "action.distribute": "Distribuisci",
          "action.distribute.horizontally": "Orizzontalmente",
          "action.distribute.horizontally.description":
            "Distribuisci orizzontalmente",
          "action.distribute.vertically": "Verticalmente",
          "action.distribute.vertically.description":
            "Distribuisci verticalmente",
          "action.effect.add": "Applica effetto",
          "action.effect.remove": "Rimuovi effetto",
          "action.enterGroup": "Entra nel gruppo",
          "action.fillType.change": "Cambia tipo di riempimento",
          "action.filter.add": "Applica filtro",
          "action.filter.remove": "Rimuovi filtro",
          "action.gradient.addStop": "Aggiungi nuovo stop al {{position}}%",
          "action.gradient.removeStop": "Elimina colore",
          "action.group": "Gruppo",
          "action.image.blur": "Applica sfocatura",
          "action.image.crop": "Ritaglia immagine",
          "action.image.effect": "Applica effetto",
          "action.image.filter": "Applica filtro",
          "action.image.inpainting": "Ripara",
          "action.image.matting": "Rimuovi sfondo",
          "action.image.matting.staging": "Rimuovi sfondo (Staging)",
          "action.image.replace": "Sostituisci immagine",
          "action.image.smartCrop": "Smart-Crop",
          "action.image.smartImage": "Funzioni intelligenti",
          "action.image.smartImage.description":
            "Azioni delle funzioni intelligenti",
          "action.image.superResolution": "Super risoluzione",
          "action.page.add": "Aggiungi blocco",
          "action.page.changeFormat": "Cambia formato blocco",
          "action.page.delete": "Elimina blocco",
          "action.pageMove.down": "Sposta giù",
          "action.pageMove.down.description": "Sposta il blocco in basso",
          "action.pageMove.left": "Sposta a sinistra",
          "action.pageMove.left.description": "Sposta il blocco a sinistra",
          "action.pageMove.right": "Sposta a destra",
          "action.pageMove.right.description": "Sposta lil blocco a destra",
          "action.pageMove.up": "Sposta su",
          "action.pageMove.up.description": "Sposta il blocco in alto",
          "action.property.reset":
            "Ripristina le proprietà dell'elemento ai valori predefiniti",
          "action.property.update": "Aggiorna la proprietà dell'elemento",
          "action.scene.load": "Carica scena",
          "action.scene.new": "Nuova scena",
          "action.selectGroup": "Seleziona gruppo",
          "action.shadow.angle.change": "Cambia angolo ombra",
          "action.shadow.angle.rotate":
            "Ruota l'ombra di 45 gradi in senso orario",
          "action.shadow.blur.change": "Cambia sfocatura ombra",
          "action.shadow.color.change": "Cambia colore ombra",
          "action.shadow.distance.change": "Cambia distanza ombra",
          "action.showInspector": "action.showInspector",
          "action.strokeCornerGeometry.change":
            "Cambia angoli di connessione della linea",
          "action.strokePosition.change": "Cambia posizione della linea",
          "action.text.autoHeight": "Abilita altezza automatica",
          "action.text.autoHeight.automatic":
            "L'altezza automatica del frame è stata abilitata",
          "action.text.change": "Cambia testo",
          "action.text.changeCase": "Cambia maiuscole/minuscole del testo",
          "action.text.fixedFrame": "Abilita frame fisso",
          "action.text.fixedFrame.automatic":
            "L'altezza del frame è stata impostata come fissa",
          "action.ungroup": "Separa gruppo",
          "action.video.replace": "Sostituisci video",
          "block.audio": "Audio",
          "block.cutout": "Ritaglio",
          "block.ellipse": "Ellisse",
          "block.graphic": "Elemento",
          "block.group": "Gruppo",
          "block.image": "Immagine",
          "block.line": "Linea",
          "block.page": "Pagina",
          "block.polygon": "Poligono",
          "block.rect": "Rettangolo",
          "block.scene": "Documento",
          "block.star": "Stella",
          "block.sticker": "Adesivo",
          "block.text": "Testo",
          "block.vectorPath": "Percorso",
          "block.video": "Video",
          "color.aqua": "Acqua",
          "color.black": "Nero",
          "color.blue": "Blu",
          "color.cyan": "Ciano",
          "color.green": "Verde",
          "color.magenta": "Magenta",
          "color.orange": "Arancione",
          "color.purple": "Viola",
          "color.red": "Rosso",
          "color.transparent": "Trasparente",
          "color.yellow": "Giallo",
          "common.advanced": "Avanzato",
          "common.arrange": "Disponi",
          "common.axis.x": "X",
          "common.axis.y": "Y",
          "common.back": "Indietro",
          "common.cancel": "Annulla",
          "common.close": "Chiudi",
          "common.color": "Colore",
          "common.colorValue": "Valore del colore",
          "common.confirm": "Conferma",
          "common.controlKey": "Ctrl",
          "common.custom": "Personalizzato",
          "common.defaultDuration": "Durata predefinita del blocco",
          "common.defaultDuration.inUnit":
            "Durata predefinita del blocco in secondi",
          "common.delete": "Elimina",
          "common.done": "Fatto",
          "common.download": "Scarica",
          "common.duplicate": "Duplica",
          "common.edit": "Modifica",
          "common.editColor": "Modifica colore",
          "common.export": "Esporta",
          "common.fill": "Riempi",
          "common.formats": "Formati",
          "common.height": "Altezza",
          "common.height.inUnit": "Altezza in {{unit}}",
          "common.hue": "Tonalità",
          "common.image": "Immagine",
          "common.inch": "Pollice",
          "common.load": "Carica",
          "common.lock": "Blocca",
          "common.millimeter": "Millimetro",
          "common.mixed": "Misto",
          "common.mode.design": "Design",
          "common.mode.preview": "Anteprima",
          "common.mode.template": "Modello",
          "common.more": "Altro",
          "common.nearestColorName": "Nome colore più vicino",
          "common.off": "Spento",
          "common.on": "Acceso",
          "common.opacity": "Opacità",
          "common.page": "Blocco",
          "common.pause": "Pausa",
          "common.pixel": "Pixel",
          "common.pixelScale": "Scala pixel",
          "common.placeholder": "Segnaposto",
          "common.play": "Riproduci",
          "common.position": "Posizione",
          "common.properties": "Proprietà",
          "common.property": "Proprietà",
          "common.redo": "Rifai",
          "common.reloadPage": "Ricarica pagina",
          "common.replace": "Sostituisci",
          "common.reset": "Ripristina",
          "common.resolution": "Risoluzione",
          "common.role": "Ruolo",
          "common.role.adopter": "Adottante",
          "common.role.creator": "Creatore",
          "common.role.presenter": "Presentatore",
          "common.role.viewer": "Visualizzatore",
          "common.rotation": "Ruota",
          "common.save": "Salva",
          "common.select": "Seleziona",
          "common.size": "Dimensione",
          "common.spotColor": "Colore spot",
          "common.srgb": "sRGB",
          "common.style": "Stile",
          "common.tint": "Tinta",
          "common.transform": "Trasforma",
          "common.trim": "Taglia",
          "common.undo": "Annulla",
          "common.unit": "Unità",
          "common.unit.description": "Unità di misura",
          "common.unlock": "Sblocca",
          "common.video": "Video",
          "common.width": "Larghezza",
          "common.width.inUnit": "Larghezza in {{unit}}",
          "component.alignAndArrange": "Sposta e Allinea",
          "component.assetPanelAutoCloseSettings": "Dopo l'Inserimento",
          "component.assetPanelAutoCloseSettings.false": "Mantieni Aperto",
          "component.assetPanelAutoCloseSettings.false.description":
            "Mantieni aperto il pannello della libreria degli asset dopo l'inserimento di un asset",
          "component.assetPanelAutoCloseSettings.true": "Chiudi",
          "component.assetPanelAutoCloseSettings.true.description":
            "Chiudi il pannello della libreria degli asset dopo l'inserimento di un asset",
          "component.assetPanelFloatingSettings": "Comportamento",
          "component.assetPanelFloatingSettings.fixed": "Fisso",
          "component.assetPanelFloatingSettings.fixed.description":
            "La libreria degli inserti è fissata affianco alla tela",
          "component.assetPanelFloatingSettings.floating": "Galleggiante",
          "component.assetPanelFloatingSettings.floating.description":
            "La libreria degli inserti galleggia sopra la tela",
          "component.assetSettings": "Asset",
          "component.assetSettings.adjustments": "Regolazioni",
          "component.assetSettings.blur": "Sfoca",
          "component.assetSettings.crop": "Ritaglia",
          "component.assetSettings.effects": "Effetti",
          "component.assetSettings.filters": "Filtri",
          "component.assetSettings.text.advanced": "Testo Avanzato",
          "component.assetSettings.text.color": "Colore del Testo",
          "component.assetSettings.image.crop": "raccolto",
          "component.assetSettings.image.filters": "filtro",
          "component.assetSettings.image.adjustments": "regolazione",
          "component.assetSettings.image.effects": "Effetti",

          "component.assetSettings.image.blur": "sfocatura",

          "component.assetSettings.transform": "Trasforma",
          "component.audio.properties": "Proprietà Audio",
          "component.audio.source": "Originale",
          "component.audio.trim": "Taglia audio",
          "component.audio.trim.description": "Regola il Taglio Audio",
          "component.audio.trim.duration": "Durata Taglio",
          "component.audio.trim.duration.description":
            "Imposta la durata dell'audio tagliato",
          "component.canvas": "Tela dell'editor",
          "component.canvas.openLibrary": "Aggiungi Elementi",
          "component.canvas.state.error":
            "Impossibile caricare il motore dell'editor",
          "component.canvas.state.loading": "Caricamento Editor",
          "component.canvas.state.loading.description": "Solo pochi secondi...",
          "component.canvas.state.unsupported": "Browser non supportato",
          "component.colorPicker.colorItem": "Elemento colore {{index}}",
          "component.colorPicker.colorItem.description":
            "Clicca per usare questo colore ({{color}})",
          "component.colorPicker.colorItem.hexInput": "Esadecimale",
          "component.colorPicker.colorItem.transparent":
            "Elemento colore {{index}}, Nome colore: trasparente",
          "component.colorPicker.description": "Cambia colore",
          "component.colorPicker.hueGradient":
            "Selezione Gradiente di Tonalità",
          "component.colorSchemeSelect.accent": "Colore di Accentuazione",
          "component.colorSchemeSelect.accent.description":
            "Cambia il colore di accentuazione",
          "component.colorSchemeSelect.active": "Colore Attivo",
          "component.colorSchemeSelect.active.description":
            "Cambia il colore attivo",
          "component.colorSchemeSelect.background": "Colore di Sfondo",
          "component.colorSchemeSelect.background.description":
            "Cambia il colore di sfondo",
          "component.contentFill.options": "Opzioni di Riempimento",
          "component.contentFill.options.description":
            "Cambia il Tipo di Riempimento",
          "component.cutout": "Impostazioni",
          "component.dnd.description":
            "I file aggiunti sono memorizzati nella libreria",
          "component.dnd.prompt": "Trascina il file qui",
          "component.dockIconSizeSelect": "Scala Icone Menù",
          "component.dockIconSizeSelect.large": "Grande",
          "component.dockIconSizeSelect.large.description":
            "Seleziona una grande scala di icone menù",
          "component.dockIconSizeSelect.normal": "Normale",
          "component.dockIconSizeSelect.normal.description":
            "Seleziona una scala normale di icone menù",
          "component.dockLabelVisibilityToggle": "Etichette Menù",
          "component.dockLabelVisibilityToggle.hide": "Nascondi",
          "component.dockLabelVisibilityToggle.hide.description":
            "Nascondi le etichette dei pulsanti menù",
          "component.dockLabelVisibilityToggle.show": "Mostra",
          "component.dockLabelVisibilityToggle.show.description":
            "Mostra le etichette dei pulsanti menù",
          "component.fileOperation.archiveScene": "Esporta Archivio",
          "component.fileOperation.exportImage": "Esporta Immagini",
          "component.fileOperation.exportPDF": "Esporta PDF",
          "component.fileOperation.exportScene": "Esporta File di Design",
          "component.fileOperation.exportVideo": "Esporta Video",
          "component.fileOperation.importScene": "Importa File di Design",
          "component.fileOperation.more": "Mostra più opzioni",
          "component.fileOperation.share": "Condividi come Modello",
          "component.inspectorPositionSelect.left": "Sinistra",
          "component.inspectorPositionSelect.left.description":
            "Seleziona che l'ispettore sia sul lato sinistro",
          "component.inspectorPositionSelect.right": "Destra",
          "component.inspectorPositionSelect.right.description":
            "Seleziona che l'ispettore sia sul lato destro",
          "component.languageSelect": "Lingua",
          "component.languageSelect.description": "Seleziona lingua",
          "component.layerList": "Navigatore",
          "component.layerList.collapseLayers": "Comprimi Lista dei Livelli",
          "component.layerList.collapsePages": "Comprimi Lista dei Blocchi",
          "component.layerList.empty": "Nessun elemento in questo blocco",
          "component.layerList.expandLayers": "Espandi Lista dei Livelli",
          "component.layerList.expandPages": "Espandi Lista dei Blocchi",
          "component.layerList.hide": "Nascondi Livello",
          "component.layerList.layers": "Livelli",
          "component.layerList.pages": "Blocchi",
          "component.library": "Libreria",
          "component.library.addFile": "Aggiungi File",
          "component.library.breadcrumbRoot": "Tutti",
          "component.library.clearSearch": "Cancella ricerca",
          "component.library.elements": "Elementi",
          "component.library.enterSection": "Entra nella sezione",
          "component.library.error":
            "Impossibile connettersi alla fonte degli asset",
          "component.library.loading": "Caricamento …",
          "component.library.noItems": "Nessun Elemento",
          "component.library.noMoreItems": "Nessun Altro Elemento",
          "component.library.removeAssetConfirmation":
            "Vuoi eliminare definitivamente questo Asset?",
          "component.library.removeAssetConfirmation.description":
            "Questa azione non può essere annullata.",
          "component.library.searchPlaceholder": "Cerca …",
          "component.librarySettings": "Librerie",
          "component.librarySettings.elementLibrary": "Libreria Elementi",
          "component.librarySettings.imageLibrary": "Libreria Immagini",
          "component.librarySettings.textLibrary": "Libreria Testo",
          "component.librarySettings.uploadLibrary": "Libreria Caricamenti",
          "component.pageSettings": "Blocco",
          "component.pageSettings.format": "Formato Blocchi",
          "component.pageSettings.manage": "Gestisci Blocchi",
          "component.pageTitleVisibilityToggle": "Titolo Blocco",
          "component.pageTitleVisibilityToggle.hide": "Nascondi",
          "component.pageTitleVisibilityToggle.hide.description":
            "Nascondi il titolo del blocco",
          "component.pageTitleVisibilityToggle.show": "Mostra",
          "component.pageTitleVisibilityToggle.show.description":
            "Mostra il titolo del blocco",
          "component.placeholder.appearance":
            "component.placeholder.appearance",
          "component.placeholder.appearance.description":
            "component.placeholder.appearance.description",
          "component.placeholder.arrange": "component.placeholder.arrange",
          "component.placeholder.arrange.description":
            "component.placeholder.arrange.description",
          "component.placeholder.disableAll":
            "component.placeholder.disableAll",
          "component.placeholder.enableAll": "component.placeholder.enableAll",
          "component.placeholder.fill": "component.placeholder.fill",
          "component.placeholder.fill.description":
            "component.placeholder.fill.description",
          "component.placeholder.general": "component.placeholder.general",
          "component.placeholder.general.description":
            "component.placeholder.general.description",
          "component.placeholder.shape": "component.placeholder.shape",
          "component.placeholder.shape.description":
            "component.placeholder.shape.description",
          "component.placeholder.stroke": "component.placeholder.stroke",
          "component.placeholder.stroke.description":
            "component.placeholder.stroke.description",
          "component.placeholder.text": "component.placeholder.text",
          "component.placeholder.text.description":
            "component.placeholder.text.description",
          "component.replacePanelAutoCloseSettings": "Dopo la Sostituzione",
          "component.replacePanelAutoCloseSettings.false": "Mantieni Aperto",
          "component.replacePanelAutoCloseSettings.false.description":
            "Mantieni aperto il pannello della libreria degli asset dopo la sostituzione di un asset",
          "component.replacePanelAutoCloseSettings.true": "Chiudi",
          "component.replacePanelAutoCloseSettings.true.description":
            "Chiudi il pannello della libreria degli asset dopo la sostituzione di un asset",
          "component.replacePanelFloatingSettings": "Comportamento",
          "component.replacePanelFloatingSettings.fixed": "Fisso",
          "component.replacePanelFloatingSettings.fixed.description":
            "La libreria di sostituzione è fissata affianco alla tela",
          "component.replacePanelFloatingSettings.floating": "Galleggiante",
          "component.replacePanelFloatingSettings.floating.description":
            "La libreria di sostituzione galleggia sopra la tela",
          "component.scalingSelect": "Dimensione UI",
          "component.scalingSelect.large": "Grande",
          "component.scalingSelect.large.description":
            "Seleziona un ridimensionamento grande",
          "component.scalingSelect.normal": "Normale",
          "component.scalingSelect.normal.description":
            "Seleziona un ridimensionamento normale",
          "component.settings.toggle": "Personalizza Editor",
          "component.settings.toggle.description":
            "Apri le impostazioni per personalizzare l'editor",
          "component.settingsPanel.appearance": "Aspetto",
          "component.settingsPanel.assetPanel": "Pannello Libreria",
          "component.settingsPanel.dock": "Menù",
          "component.settingsPanel.documentation": "Documentazione",
          "component.settingsPanel.general": "Generale",
          "component.settingsPanel.header": "Personalizza Editor",
          "component.settingsPanel.inspectorPanel": "Pannello Ispeziona",
          "component.settingsPanel.replacePanel": "Pannello Sostituzione",
          "component.themeSelect": "Tema",
          "component.themeSelect.dark": "Scuro",
          "component.themeSelect.dark.description": "Seleziona il tema scuro",
          "component.themeSelect.dialog":
            "Proprietà CSS personalizzate generate",
          "component.themeSelect.generate": "Genera CSS",
          "component.themeSelect.light": "Chiaro",
          "component.themeSelect.light.description": "Seleziona il tema chiaro",
          "component.timeline.audio.options.description":
            "Opzioni Elemento Audio",
          "component.timeline.scale.down": "Riduci",
          "component.timeline.scale.down.description":
            "Riduci la scala temporale",
          "component.timeline.scale.label": "Scala Temporale",
          "component.timeline.scale.up": "Ingrandisci",
          "component.timeline.scale.up.description":
            "Ingrandisci la scala temporale",
          "component.timeline.video.options.description":
            "Opzioni Elemento Video",
          "component.video.properties": "Proprietà Video",
          "component.video.source": "Originale",
          "component.video.trim": "Taglia video",
          "component.video.trim.description": "Regola il taglio video",
          "component.video.trim.duration": "Durata Taglio",
          "component.video.trim.duration.description":
            "Imposta la durata del video tagliato",
          "component.video.unsupported": "Video Non Supportato",
          "component.video.unsupported.description":
            "<p>Non supportiamo l'editing video per il tuo browser attuale.</p><p>Mentre stiamo lavorando per supportare tutti i principali browser, utilizza un browser supportato come l'ultima versione di Google Chrome, Microsoft Edge o un altro browser basato su Chromium.</p>",
          "component.viewSelect": "Vista",
          "component.viewSelect.advanced": "Avanzato",
          "component.viewSelect.advanced.description":
            "Mostra sempre l'ispettore",
          "component.viewSelect.default": "Predefinita",
          "component.viewSelect.default.description":
            "Nascondi l'ispettore se possibile",
          "component.welcome.text":
            "<p>Grazie di utilizzare il nostro tool!</p><p>Siate creativi aggiungendo un'immagine, del testo o degli elementi dalla libreria degli asset.</p>",
          "component.zoom.autoFit": "Adatta automaticamente il blocco",
          "component.zoom.fitPage": "Adatta blocco",
          "component.zoom.fitSelection": "Adatta selezione",
          "component.zoom.in": "Ingrandisci",
          "component.zoom.label.auto": "Auto",
          "component.zoom.options": "Vedi più opzioni di zoom",
          "component.zoom.out": "Riduci",
          "component.zoom.shortcut": "o premi {{shortcut}}",
          "component.zoom.to": "Zoom al {{percentage}}%",
          "error.applyAsset": "Errore nell'Applicare l'Asset",
          "error.applyAsset.description":
            "Abbiamo riscontrato un errore durante il tentativo di applicare l'asset",
          "error.cta.generic": "Errore",
          "error.cta.generic.description":
            "L'azione ha incontrato un errore sconosciuto. Riprova.",
          "error.generic": "Errore Sconosciuto",
          "error.generic.description":
            "L'applicazione ha incontrato un errore sconosciuto. Prova a ricaricare la pagina",
          "error.replaceAsset": "Errore nella Sostituzione dell'Asset",
          "error.replaceAsset.description":
            "Abbiamo riscontrato un errore durante il tentativo di sostituire l'asset",
          "error.upload": "Impossibile caricare il file",
          "error.upload.description": "Il file non può essere caricato",
          "error.upload.sizeExceeded":
            "Il file supera le dimensioni massime di {{limit}}x{{limit}}",
          "input.adjustments": "Regolazioni",
          "input.adjustments.basic": "Base",
          "input.adjustments.refinements": "Perfezionamenti",
          "input.adjustments.tooltip": "Cambia regolazioni",
          "input.alwaysOnBottom": "Sempre in fondo",
          "input.alwaysOnTop": "Fisso in primo piano",
          "input.appearance": "Aspetto",
          "input.aspectLock": "Blocca proporzioni",
          "input.aspectLock.description": "Attiva/disattiva blocco proporzioni",
          "input.audio.duration.description": "Durata audio",
          "input.audio.duration.reset": "Ripristina durata audio",
          "input.audio.mute": "Audio muto",
          "input.bleedMargin.select": "Seleziona margini di sanguinamento",
          "input.blur": "Sfoca",
          "input.blur.tooltip": "Cambia sfocatura",
          "input.booleanoperations": "Combina",
          "input.booleanoperations.exclude": "Escludi",
          "input.booleanoperations.intersect": "Interseca",
          "input.booleanoperations.subtract": "Sottrai",
          "input.booleanoperations.union": "Unisci",
          "input.canvas": "Tela",
          "input.character": "Carattere",
          "input.clipContent": "Clip Contenuto",
          "input.clipContent.off.description":
            "Disattiva la clip del contenuto",
          "input.clipContent.on.description": "Attiva la clip del contenuto",
          "input.clipLines": "Linee di ritaglio",
          "input.clipLines.off.description": "Linee di ritaglio disattivate",
          "input.clipLines.on.description": "Linee di ritaglio attivate",
          "input.colorMode": "Modalità colore",
          "input.colorMode.description": "Scegli la modalità colore",
          "input.cutoutOffset": "Offset",
          "input.cutoutSmoothing": "Smoothing",
          "input.cutoutType": "Tipo",
          "input.cutoutType.dashed": "Forato",
          "input.cutoutType.solid": "Taglio",
          "input.duration": "Durata",
          "input.duration.description": "Durata in secondi",
          "input.effect": "Effetto",
          "input.effect.tooltip": "Cambia effetto",
          "input.export": "Esporta",
          "input.filter": "Filtro",
          "input.filter.tooltip": "Cambia filtro",
          "input.fontSelect.fallback": "Predefinito",
          "input.fontSelect.tooltip": "Cambia stile del carattere",
          "input.fontSize.select": "Seleziona dimensione del carattere",
          "input.fontSize.selectMax": "input.fontSize.selectMax",
          "input.fontSize.selectMin": "input.fontSize.selectMin",
          "input.gradient.activateColorStop": "Premi spazio per attivare",
          "input.gradient.colorPosition": "Posizione colore",
          "input.gradient.colorStop": "Punto colore",
          "input.gradient.colorStop.active": "Punto colore attivo",
          "input.gradient.flip": "Inverti ordine punto colore gradiente",
          "input.gradient.gradientAngle": "Angolo gradiente",
          "input.gradient.rotate":
            "Ruota gradiente di 45 gradi in senso orario",
          "input.hspSelectiveAdjustments": "Perfezionamenti Selettivi",
          "input.insertVariable": "Inserisci variabile",
          "input.layer": "Livello",
          "input.multiSelection.notice": "input.multiSelection.notice",
          'is not supported yet"': "non è ancora supportato",
          'gleichzeitig bearbeitet werden"': "gleichzeitig bearbeitet werden",
          "input.options": "Opzioni",
          "input.options.description": "Altre opzioni",
          "input.pages": "Blocchi",
          "input.preset": "Formato",
          "input.preset.description": "Preset formato blocco",
          "input.propertyToggle.hidden": "Nascosto",
          "input.propertyToggle.visible": "Visibile",
          "input.selection": "input.selection",
          "input.shadow.angle": "Angolo",
          "input.shadow.angle.description": "Angolo ombra",
          "input.shadow.blur": "Sfocatura",
          "input.shadow.blur.description": "Sfocatura ombra",
          "input.shadow.distance": "Distanza",
          "input.shadow.distance.description": "Distanza ombra",
          "input.shadow.label": "Ombra",
          "input.shadow.valueLabel": "Ombra",
          "input.shape": "Forma",
          "input.shape.options": "Opzioni forma",
          "input.showInExport": "Mostra in esportazione",
          "input.sliderInput.toggleNumberInput":
            "Visualizza input valore diretto",
          "input.stroke": "Bordo",
          "input.text.advanced.description":
            "Altezza linea, spaziatura, allineamento e dimensioni",
          "input.text.placeholder": "Scrivi qualcosa",
          "input.time.end": "Fine tempo",
          "input.time.start": "Inizio tempo",
          "input.transform.description": "Regola posizione e dimensione",
          "input.typefaceSelect.tooltip": "Cambia tipo di carattere",
          "input.uploadAudio": "Caricamento audio...",
          "input.uploadFiles": "Caricamento file...",
          "input.uploadImage": "Caricamento immagine...",
          "input.uploadVideo": "Caricamento video...",
          "input.video": "Video",
          "input.video.duration.description": "Durata video",
          "input.video.duration.reset": "Ripristina durata video",
          "libraries.ly.img.audio.label": "Audio",
          "libraries.ly.img.audio.upload.label": "Audio Caricati",
          "libraries.ly.img.colors.defaultPalette.label": "Colori predefiniti",
          "libraries.ly.img.image.label": "Immagini",
          "libraries.ly.img.image.upload.label": "Immagini Caricate",
          "libraries.ly.img.local.label": "Locale",
          "libraries.ly.img.sticker.doodle.label": "Bozzetti",
          "libraries.ly.img.sticker.emoji.label": "Emoji",
          "libraries.ly.img.sticker.emoticons.label": "Emoticon",
          "libraries.ly.img.sticker.hand.label": "Mani",
          "libraries.ly.img.sticker.label": "Adesivi",
          "libraries.ly.img.template.confirmation.body":
            "Il tuo contenuto, come immagini e testo, non può essere adottato e tutti i contenuti saranno eliminati.",
          "libraries.ly.img.template.confirmation.confirm":
            "Sì, elimina le modifiche",
          "libraries.ly.img.template.confirmation.headline":
            "Sostituire il design attuale?",
          "libraries.ly.img.template.label": "Modelli",
          "libraries.ly.img.text.headline.label": "Sotto",
          "libraries.ly.img.text.label": "Testo",
          "libraries.ly.img.text.paragraph.label": "Paragrafo",
          "libraries.ly.img.text.title.label": "Titolo",
          "libraries.ly.img.upload.label": "Carica",
          "libraries.ly.img.vectorpath.abstract.label": "Astratte",
          "libraries.ly.img.vectorpath.label": "Forme",
          "libraries.ly.img.vectorpath.vectorpaths.label": "Base",
          "libraries.ly.img.video.label": "Video",
          "libraries.ly.img.video.upload.label": "Video Caricati",
          "libraries.unsplash.label": "Unsplash",
          "meta.currentLanguage": "Italiano",
          "preset.document.american-legal": "Legale",
          "preset.document.american-letter": "Lettera",
          "preset.document.business-card": "Biglietto da visita",
          "preset.document.custom": "Personalizzato",
          "preset.document.din-a0": "DIN A0",
          "preset.document.din-a1": "DIN A1",
          "preset.document.din-a2": "DIN A2",
          "preset.document.din-a3": "DIN A3",
          "preset.document.din-a4": "DIN A4",
          "preset.document.din-a5": "DIN A5",
          "preset.document.din-a6": "DIN A6",
          "preset.document.din-a65": "DIN A6/5",
          "preset.document.din-b5": "DIN B5",
          "preset.document.format2k": "Video 2k 1080P 1:1.77",
          "preset.document.format4k": "4K (Ultra HD) 4K / 2160p 16:9",
          "preset.document.fullhd": "Full HD (FHD) 1080p 16:9",
          "preset.document.hd": "HD (Alta definizione) 720p 16:9",
          "preset.document.instagram-photo": "Foto Instagram",
          "preset.document.instagram-profile": "Profilo Instagram",
          "preset.document.instagram-story": "Storia Instagram",
          "preset.document.qhd": "QHD (Quad HD) 1440p 16:9",
          "preset.document.social-feed": "Feed social",
          "preset.document.social-story": "Storia / Reel",
          "preset.document.square": "Quadrato",
          "preset.document.twitter-header": "Header Twitter",
          "preset.document.twitter-image": "Immagine Twitter",
          "preset.document.twitter-profile": "Profilo Twitter",
          "preset.template.blank_1": "Vuoto",
          "preset.template.business_card_1": "Biglietto da visita",
          "preset.template.collage_1": "Collage",
          "preset.template.instagram_photo_1": "Foto Instagram",
          "preset.template.instagram_story_1": "Storia Instagram",
          "preset.template.postcard_1": "Cartolina 1",
          "preset.template.postcard_2": "Cartolina 2",
          "preset.template.poster_1": "Poster",
          "preset.template.presentation_4": "Presentazione",
          "property.adjustments.blacks": "Neri",
          "property.adjustments.brightness": "Luminosità",
          "property.adjustments.clarity": "Lucentezza",
          "property.adjustments.contrast": "Contrasto",
          "property.adjustments.exposure": "Esposizione",
          "property.adjustments.gamma": "Gamma",
          "property.adjustments.highlights": "Evidenziazioni",
          "property.adjustments.saturation": "Saturazione",
          "property.adjustments.shadows": "Ombre",
          "property.adjustments.sharpness": "Nitidezza",
          "property.adjustments.temperature": "Temperatura",
          "property.adjustments.whites": "Bianchi",
          "property.autoSize": "Dimensioni cornice",
          "property.autoSize.autoHeight.description":
            "Ridimensiona automaticamente l'altezza della cornice del testo",
          "property.autoSize.fixedFrame.description":
            "Mantieni la cornice del testo",
          "property.backgroundColor": "Sfondo",
          "property.backgroundColor.description": "Cambia colore di sfondo",
          "property.bleedMargin": "Margine sanguinamento",
          "property.blendMode": "Modalità miscelazione",
          "property.blendMode.Color": "Colore",
          "property.blendMode.ColorBurn": "Color Burn",
          "property.blendMode.ColorDodge": "Color Dodge",
          "property.blendMode.Darken": "Oscura",
          "property.blendMode.Difference": "Differenza",
          "property.blendMode.Exclusion": "Esclusione",
          "property.blendMode.HardLight": "Hard Light",
          "property.blendMode.Hue": "Tonalità",
          "property.blendMode.Lighten": "Schiarisci",
          "property.blendMode.Luminosity": "Luminosità",
          "property.blendMode.Multiply": "Moltiplica",
          "property.blendMode.Normal": "Normale",
          "property.blendMode.Overlay": "Sovrapponi",
          "property.blendMode.PassThrough": "Passa attraverso",
          "property.blendMode.Saturation": "Saturazione",
          "property.blendMode.Screen": "Schermo",
          "property.blendMode.SoftLight": "Soft Light",
          "property.blendMode.tooltip": "Cambia modalità di miscelazione",
          "property.blur.extrudeBlur": "Sfocatura sporgente",
          "property.blur.extrudeBlur.amount": "Intensità",
          "property.blur.linearBlur": "Sfocatura lineare",
          "property.blur.linearBlur.blurRadius": "Intensità",
          "property.blur.linearBlur.x1": "Punto 1 - X",
          "property.blur.linearBlur.x2": "Punto 2 - X",
          "property.blur.linearBlur.y1": "Punto 1 - Y",
          "property.blur.linearBlur.y2": "Punto 2 - Y",
          "property.blur.mirroredBlur": "Sfocatura specchiata",
          "property.blur.mirroredBlur.blurRadius": "Intensità",
          "property.blur.mirroredBlur.gradientSize": "Dimensione del gradiente",
          "property.blur.mirroredBlur.size": "Dimensione dell'area non sfocata",
          "property.blur.mirroredBlur.x1": "Punto 1 - X",
          "property.blur.mirroredBlur.x2": "Punto 2 - X",
          "property.blur.mirroredBlur.y1": "Punto 1 - Y",
          "property.blur.mirroredBlur.y2": "Punto 2 - Y",
          "property.blur.radialBlur": "Sfocatura radiale",
          "property.blur.radialBlur.blurRadius": "Intensità",
          "property.blur.radialBlur.gradientRadius": "Dimensione del gradiente",
          "property.blur.radialBlur.radius": "Dimensione dell'area non sfocata",
          "property.blur.radialBlur.x": "Punto - X",
          "property.blur.radialBlur.y": "Punto - Y",
          "property.blur.uniformBlur": "Sfocatura gaussiana",
          "property.blur.uniformBlur.intensity": "Intensità",
          "property.color": "Colore",
          "property.color.description": "Cambia colore",
          "property.crop": "Taglia",
          "property.crop.scale": "Scala",
          "property.crop.scale.description":
            "Scala proporzionalmente le dimensioni dell'immagine",
          "property.crop.size": "Area di ritaglio",
          "property.crop.straighten": "Raddrizza",
          "property.crop.tooltip": "Modifica le proprietà di ritaglio",
          "property.duotoneFilter.breezy": "Breezy",
          "property.duotoneFilter.clash": "Clash",
          "property.duotoneFilter.deepblue": "Blu profondo",
          "property.duotoneFilter.desert": "Deserto",
          "property.duotoneFilter.frog": "Rana",
          "property.duotoneFilter.peach": "Pesca",
          "property.duotoneFilter.plum": "Prugna",
          "property.duotoneFilter.sunset": "Tramonto",
          "property.duoToneFilterIntensity": "Intensità",
          "property.effect.crossCut": "Taglio incrociato",
          "property.effect.crossCut.offset": "Spostamento orizzontale",
          "property.effect.crossCut.slices": "Tagli orizzontali",
          "property.effect.crossCut.speedV": "Spostamento verticale",
          "property.effect.crossCut.time": "Variazione",
          "property.effect.dotPattern": "Modello a punti",
          "property.effect.dotPattern.blur": "Sfocatura globale",
          "property.effect.dotPattern.dots": "Numero di punti",
          "property.effect.dotPattern.size": "Dimensione dei punti",
          "property.effect.extrudeBlur": "Sfocatura in rilievo",
          "property.effect.extrudeBlur.amount": "Intensità",
          "property.effect.glow": "Bagliore",
          "property.effect.glow.amount": "Intensità",
          "property.effect.glow.darkness": "Oscuramento",
          "property.effect.glow.size": "Bagliore",
          "property.effect.halfTone": "Mezzatinta",
          "property.effect.halfTone.angle": "Angolo del modello",
          "property.effect.halfTone.scale": "Scala del modello",
          "property.effect.joggle.amount": "Gravità",
          "property.effect.joggle.time": "Variazione",
          "property.effect.linocut": "Linocut",
          "property.effect.linocut.scale": "Scala del modello",
          "property.effect.liquid": "Liquido",
          "property.effect.liquid.amount": "Intensità",
          "property.effect.liquid.scale": "Scala",
          "property.effect.liquid.speed": "Sensibilità",
          "property.effect.liquid.time": "Variazione",
          "property.effect.mirror": "Specchio",
          "property.effect.mirror.side": "Lato specchiato",
          "property.effect.outliner": "Contorno",
          "property.effect.outliner.amount": "Intensità",
          "property.effect.outliner.passthrough": "Sfumatura",
          "property.effect.pixelize": "Pixelizzazione",
          "property.effect.pixelize.horizontalPixelSize":
            "Conteggio orizzontale",
          "property.effect.pixelize.verticalPixelSize": "Conteggio verticale",
          "property.effect.posterize": "Posterizzazione",
          "property.effect.posterize.levels": "Numero di livelli",
          "property.effect.psychedelic.amount": "Intensità",
          "property.effect.psychedelic.offset": "Spostamento diagonale",
          "property.effect.psychedelic.time": "Variazione",
          "property.effect.radialPixel": "Pixel radiale",
          "property.effect.radialPixel.radius": "Raggio per riga",
          "property.effect.radialPixel.segments": "Dimensione per riga",
          "property.effect.radiate.centerBrightness": "Luminosità al centro",
          "property.effect.radiate.colorize": "Saturazione",
          "property.effect.radiate.powerCurve": "Intensità",
          "property.effect.scanlines.count": "Altezza linea",
          "property.effect.scanlines.linesAmount": "Intensità delle linee",
          "property.effect.scanlines.noiseAmount":
            "Quantità di rumore applicato",
          "property.effect.scanlines.time": "Variazione",
          "property.effect.separate.amount": "Spostamento",
          "property.effect.separate.time": "Variazione",
          "property.effect.sharpie": "Sharpie",
          "property.effect.shifter": "Spostamento",
          "property.effect.shifter.amount": "Distanza",
          "property.effect.shifter.angle": "Direzione dello spostamento",
          "property.effect.tiltShift": "Tiltshift",
          "property.effect.tiltShift.amount": "Intensità",
          "property.effect.tiltShift.position": "Posizione",
          "property.effect.tvGlitch": "Disturbo TV",
          "property.effect.tvGlitch.distortion": "Distorsione grezza",
          "property.effect.tvGlitch.distortion2": "Distorsione fine",
          "property.effect.tvGlitch.rollSpeed": "Spostamento verticale",
          "property.effect.tvGlitch.speed": "Variazione",
          "property.effect.tvGlitch.time": "Variazione",
          "property.effect.vignette": "Vignetta",
          "property.effect.vignette.darkness": "Colore",
          "property.effect.vignette.offset": "Dimensione",
          "property.effect.waves.size": "Dimensione del modello",
          "property.effect.waves.speed": "Sensibilità",
          "property.effect.waves.strength": "Intensità",
          "property.effect.waves.time": "Variazione",
          "property.fill": "Riempimento",
          "property.fill.description": "Cambia riempimento",
          "property.fillType": "Tipo di riempimento",
          "property.fillType.gradient": "Gradiente",
          "property.fillType.gradient.description":
            "Usa un riempimento a gradiente",
          "property.fillType.solid": "Solido",
          "property.fillType.solid.description":
            "Usa un colore di riempimento solido",
          "property.flip": "Capovolgi",
          "property.flip.x": "Capovolgi orizzontalmente",
          "property.flip.y": "Capovolgi verticalmente",
          "property.gradientType.conical": "Gradiente conico",
          "property.gradientType.linear": "Gradiente lineare",
          "property.gradientType.radial": "Gradiente radiale",
          "property.hspSelectiveAdjustments.hue": "Tonalità",
          "property.hspSelectiveAdjustments.perceivedBrightness": "Luminosità",
          "property.hspSelectiveAdjustments.saturation": "Saturazione",
          "property.innerDiameter": "Diametro interno",
          "property.layout": "Layout",
          "property.layout.horizontal": "Disponi i blocchi in orizzontale",
          "property.layout.vertical": "Disponi i blocchi in verticale",
          "property.letterSpacing": "Spaziatura delle lettere",
          "property.lineHeight": "Altezza della linea",
          "property.lineWidth": "Larghezza della linea",
          "property.lineWidth.description": "Cambia la larghezza della linea",
          "property.lutFilter.ad1920": "1920 D.C.",
          "property.lutFilter.ancient": "Antico",
          "property.lutFilter.bleached": "Kalmen",
          "property.lutFilter.bleachedblue": "Joran",
          "property.lutFilter.blues": "Polaroid",
          "property.lutFilter.blueshadows": "Zephyr",
          "property.lutFilter.breeze": "Levante",
          "property.lutFilter.bw": "In bianco e nero",
          "property.lutFilter.celsius": "Inferno",
          "property.lutFilter.chest": "Chestnut",
          "property.lutFilter.classic": "Classico",
          "property.lutFilter.colorful": "Colorato",
          "property.lutFilter.cool": "Snappy",
          "property.lutFilter.cottoncandy": "Candy",
          "property.lutFilter.creamy": "Creamy",
          "property.lutFilter.eighties": "Low Fire",
          "property.lutFilter.elder": "Colla",
          "property.lutFilter.evening": "Sunrise",
          "property.lutFilter.fall": "Moss",
          "property.lutFilter.fixie": "Fixie",
          "property.lutFilter.food": "Food",
          "property.lutFilter.fridge": "Fridge",
          "property.lutFilter.front": "Sunny 70s",
          "property.lutFilter.glam": "Glam",
          "property.lutFilter.gobblin": "Gobblin",
          "property.lutFilter.highcarb": "High Carb",
          "property.lutFilter.highcontrast": "Hicon",
          "property.lutFilter.k1": "K1",
          "property.lutFilter.k2": "Flat Black",
          "property.lutFilter.k6": "K6",
          "property.lutFilter.kdynamic": "Pebble",
          "property.lutFilter.keen": "Keen",
          "property.lutFilter.lenin": "Lemon",
          "property.lutFilter.litho": "Litho",
          "property.lutFilter.lomo": "Lomo",
          "property.lutFilter.lomo100": "Lomo 100",
          "property.lutFilter.lucid": "Lucid",
          "property.lutFilter.mellow": "Mellow",
          "property.lutFilter.neat": "Neat",
          "property.lutFilter.nogreen": "Pumpkin",
          "property.lutFilter.orchid": "Solanus",
          "property.lutFilter.pale": "Pallido",
          "property.lutFilter.pitched": "Pitched",
          "property.lutFilter.plate": "Weathered",
          "property.lutFilter.pola669": "Green Gap",
          "property.lutFilter.polasx": "Pola SX",
          "property.lutFilter.pro400": "Pro 400",
          "property.lutFilter.quozi": "Quozi",
          "property.lutFilter.sepiahigh": "Sepia",
          "property.lutFilter.settled": "Settled",
          "property.lutFilter.seventies": "Anni settanta",
          "property.lutFilter.sin": "Hard Stuff",
          "property.lutFilter.soft": "Soft",
          "property.lutFilter.steel": "Acciaio",
          "property.lutFilter.summer": "Estate",
          "property.lutFilter.sunset": "Dorato",
          "property.lutFilter.tender": "Tenero",
          "property.lutFilter.texas": "Oldtimer",
          "property.lutFilter.twilight": "Twilight",
          "property.lutFilter.winter": "Softy",
          "property.lutFilter.x400": "Dusty",
          "property.lutFilterIntensity": "Intensità",
          "property.opacity": "Opacità",
          "property.orientation": "Orientamento",
          "property.orientation.flip": "Capovolgi orientamento",
          "property.orientation.landscape": "Paesaggio",
          "property.orientation.portrait": "Ritratto",
          "property.orientation.square": "Quadrato",
          "property.outlineColor": "Contorno",
          "property.outlineWidth": "Larghezza del contorno",
          "property.paragraphSpacing": "Spaziatura paragrafo",
          "property.replace.description": "Comportati come segnaposto",

          "property.placeholderBehavior.description":
            "Comportati come segnaposto",
          "property.design.arrange.description": "Comportati come segnaposto",
          "property.points": "Punti",
          "property.sides": "Lati",
          "property.strokeColor": "Colore contorno",
          "property.strokeColor.description": "Cambia il colore del contorno",
          "property.strokeCornerGeometry": "Unisci",
          "property.strokeCornerGeometry.bevel": "Smussato",
          "property.strokeCornerGeometry.description":
            "Cambia lo stile di unione del contorno",
          "property.strokeCornerGeometry.miter": "Miter",
          "property.strokeCornerGeometry.round": "Arrotondato",
          "property.strokePosition.center": "Centro",
          "property.strokePosition.description":
            "Cambia la posizione del contorno",
          "property.strokePosition.inner": "All'interno",
          "property.strokePosition.outer": "All'esterno",
          "property.strokePositionAndCornerGeometry.description":
            "Cambia la posizione e lo stile di unione del contorno",
          "property.strokeStyle": "Stile",
          "property.strokeStyle.dashed": "Tratteggiato",
          "property.strokeStyle.dashedRound": "Tratteggiato rotondo",
          "property.strokeStyle.description": "Cambia lo stile del contorno",
          "property.strokeStyle.dotted": "Puntinato",
          "property.strokeStyle.longDashed": "Tratteggiato lungo",
          "property.strokeStyle.longDashedRound": "Tratteggiato lungo rotondo",
          "property.strokeStyle.solid": "Continuo",
          "property.strokeWidth": "Larghezza contorno",
          "property.strokeWidth.description":
            "Cambia la larghezza del contorno",
          "property.textAlignment.horizontal": "Allineamento orizzontale",
          "property.textAlignment.horizontal.center":
            "Allinea il testo al centro",
          "property.textAlignment.horizontal.description":
            "Cambia l'allineamento orizzontale",
          "property.textAlignment.horizontal.left":
            "Allinea il testo a sinistra",
          "property.textAlignment.horizontal.right":
            "Allinea il testo a destra",
          "property.textAlignment.vertical": "Allineamento verticale",
          "property.textAlignment.vertical.bottom": "Allinea il testo in basso",
          "property.textAlignment.vertical.center":
            "Allinea il testo al centro",
          "property.textAlignment.vertical.top": "Allinea il testo in alto",
          "property.textCase": "Maiuscole/minuscole",
          "property.textCase.lowercase": "Minuscolo",
          "property.textCase.normal": "Nessuna modifica",
          "property.textCase.titlecase": "Iniziali maiuscole",
          "property.textCase.uppercase": "Maiuscolo",
          "property.volume": "Volume",
          "scope.appearance.adjustments": "scope.appearance.adjustments",
          "scope.appearance.blur": "scope.appearance.blur",
          "scope.appearance.effect": "scope.appearance.effect",
          "scope.appearance.filter": "scope.appearance.filter",
          "scope.appearance.shadow": "scope.appearance.shadow",
          "scope.editor.select": "Consenti la selezione",
          "scope.fill.change": "scope.fill.change",
          "scope.fill.changeType": "scope.fill.changeType",
          "scope.layer.blendMode": "scope.layer.blendMode",
          "scope.layer.crop": "Consenti il ritaglio",
          "scope.design.arrange": "Consenti Ridimesionamento",
          "scope.content.replace": "Consenti Modifica Contenuto",
          "scope.design.style": "Consenti Modifica Stile",
          "scope.layer.flip": "Consenti il capovolgimento",
          "scope.layer.move": "Consenti lo spostamento",
          "scope.layer.opacity": "scope.layer.opacity",
          "scope.layer.resize": "Consenti la ridimensionamento",
          "scope.layer.rotate": "Consenti la rotazione",
          "scope.lifecycle.destroy": "Consenti l'eliminazione",
          "scope.lifecycle.duplicate": "Consenti la duplicazione",
          "scope.shape.change": "scope.shape.change",
          "scope.stroke.change": "scope.stroke.change",
          "scope.text.character": "scope.text.character",
          "scope.text.edit": "scope.text.edit",
          "typography.autoSize": "Auto-dimensionamento",
          "typography.autoSize.abbreviation": "Auto",
          "typography.bold": "Grassetto",
          "typography.italic": "Corsivo",
          "typography.normal": "Normale",
          "typography.size": "Dimensione",
          "typography.sizeRange": "Intervallo di dimensioni",
          "typography.style": "Stile",
          "typography.typeface": "Carattere",
          "typography.weight.100": "Sottile",
          "typography.weight.200": "Extra leggero",
          "typography.weight.300": "Leggero",
          "typography.weight.400": "Normale",
          "typography.weight.500": "Medio",
          "typography.weight.600": "Semi grassetto",
          "typography.weight.700": "Grassetto",
          "typography.weight.800": "Extra grassetto",
          "typography.weight.900": "Nero",
          "variables.address.label": "Indirizzo",
          "variables.city.label": "Città",
          "variables.company_name.label": "Azienda",
          "variables.first_name.label": "Nome",
          "variables.last_name.label": "Cognome",
          "warning.invalidType": "Tipo non valido: Tipo di file non consentito",
        },
      },
      ui: {
        pageFormats: formatAssetsToPresets(PAGE_FORMAT_ASSETS),
        elements: {
          view: "advanced",
          panels: {
            inspector: {
              show: true,
              position: "right",
            },
            settings: true,
          },
          dock: {
            iconSize: "normal",
            hideLabels: false,
            groups: [
              {
                id: "my-templates-group",
                entryIds: ["my-templates-entry"],
              },
              {
                id: "misc",
                entryIds: ["pageFormats"],
              },
              {
                id: "examples",
                entryIds: ["ly.img.video.templates"],
              },
              {
                id: "ly.img.defaultGroup",
                showOverview: true,
              },
            ],
          },
          libraries: {
            insert: {
              entries: (defaultEntries) => {
                return [
                  ...defaultEntries,
                  PAGE_FORMATS_INSERT_ENTRY,
                  // {
                  //   id: "ly.img.video.templates",
                  //   sourceIds: ["ly.img.video.templates"],
                  //   icon: () => caseAssetPath("/static-video-scenes-icon.svg"),
                  // },
                ];
              },
            },
          },
          navigation: {
            position: UserInterfaceElements.NavigationPosition.Top,
            action: {
              save: true,
              load: true,
              download: true,
              export: true,
            },
          },
        },
      },
      callbacks: {
        onUpload: async (files) => {
          // Check if files is a valid File object or array of File objects
          if (
            !(
              files instanceof File ||
              (Array.isArray(files) && files.every((f) => f instanceof File))
            )
          ) {
            console.error("Invalid files parameter:", files);
            throw new Error("Invalid files parameter");
          }

          const formData = new FormData();
          // If files is an array, append each file individually
          if (Array.isArray(files)) {
            files.forEach((file) => formData.append("files", file));
          } else {
            formData.append("files", files);
          }

          try {
            const response = await axios.post(
              "https://dev-api.wipovtool.com/api/v1/admin/file-upload/upload",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            const { data } = response.data;

            if (!Array.isArray(data) || data.length === 0) {
              console.error("Invalid response data:", data);
              throw new Error("Invalid response data");
            }

            const filePath = data[0]; // Assuming the first item in the array is the file path

            const newImage = {
              id: "wipov_toop_image",
              meta: {
                uri: "https://dev-api.wipovtool.com" + filePath,
                thumbUri: "https://dev-api.wipovtool.com" + filePath,
              },
            };

            return newImage;
          } catch (error) {
            if (error.response) {
              console.error("Server error:", error.response.data);
              console.error("Status code:", error.response.status);
            } else if (error.request) {
              console.error("No response received:", error.request);
            } else {
              console.error("Error setting up request:", error.message);
            }
            throw new Error("Upload failed");
          }
        },
        onSave: async (scene) => {
          const fileName = prompt(
            "Please enter a name for your file:",
            "cesdk-scene"
          );
          if (!fileName) {
            console.log("Save canceled, no file name provided.");
            return;
          }

          const base64Data = btoa(unescape(encodeURIComponent(scene)));

          try {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new Blob([byteArray], {
              type: "application/octet-stream",
            });

            const formData = new FormData();
            formData.append("files", file, `${fileName}.scene`);

            const response = await axios.post(
              "https://dev-api.wipovtool.com/api/v1/admin/file-upload/upload",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  accept: "application/json",
                },
              }
            );

            console.log("File uploaded successfully", response.data);
          } catch (error) {
            console.error("Error uploading file", error);
          }
        },
        onLoad: "upload",
        onExport: "download",
      },
    };

    let cesdk;
    if (cesdk_container.current) {
      CreativeEditorSDK.create(cesdk_container.current, config).then(
        async (instance) => {
          instance.addDefaultAssetSources();
          instance.addDemoAssetSources({
            sceneMode: "Video",
            excludeAssetSourceIds: ["ly.img.audio"],
          });

          const { imageUrls, videoUrls } = await fetchAssetsImageUrlsFromAPI();

          // Loop through each image URL and add it as a custom asset
          for (let index = 0; index < imageUrls.length; index++) {
            const imageUrl = imageUrls[index];

            // Adding a custom image asset
            await instance.engine.asset.addAssetToSource(
              "ly.img.image.upload",
              {
                id: `img${index}`,
                label: `Image ${index + 1}`,
                meta: {
                  uri: decodeURIComponent(imageUrl), // Use fetched URL
                  thumbUri: imageUrl, // Use fetched URL
                  mimeType: "image/png", // Use correct MIME type
                },
              }
            );
          }

          for (let index = 0; index < videoUrls.length; index++) {
            const videoUrl = videoUrls[index];

            // Adding a custom image asset
            await instance.engine.asset.addAssetToSource(
              "ly.img.video.upload",
              {
                id: `vid${index}`,
                label: `video ${index + 1}`,
                meta: {
                  uri: videoUrl, // Use fetched URL
                  // thumbUri: videoUrl, // Use fetched URL
                  mimeType: "video/mp4", // Use correct MIME type
                },
              }
            );
          }

          cesdk = instance;
          cesdkRef.current = instance;
          cesdk.engine.editor.setSettingBool("page/title/show", false);
          const scene = await cesdk.createVideoScene();

          AddAnimationComponent(instance);

          loadAssetSourceFromContentJSON(
            cesdk.engine,
            VIDEO_SCENES_ASSETS,
            caseAssetPath("/templates"),
            createDefaultApplyAssetScene(cesdk.engine)
          );

          loadAssetSourceFromContentJSON(
            cesdk.engine,
            AUDIO_ASSETS,
            caseAssetPath("/audio")
          );
          loadAssetSourceFromContentJSON(
            cesdk.engine,
            PAGE_FORMAT_ASSETS,
            caseAssetPath("/page-formats"),
            createApplyFormatAsset(cesdk.engine)
          );
        }
      );
    }
    return () => {
      if (cesdk) {
        cesdk.dispose();
      }
    };
  }, []);

  return (
    <div style={cesdkWrapperStyle}>
      <div ref={cesdk_container} style={cesdkStyle}></div>
    </div>
  );
};

const cesdkStyle = {
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const cesdkWrapperStyle = {
  position: "relative",
  overflow: "hidden",
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  width: "100vw",
};

export default CaseComponent;
